"use client";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotificationsWS } from "@/hooks/use-notifications-ws";
import {
  inAppNotificationsApi,
  InAppNotification,
} from "@/lib/client/notifications-api";
import { cn } from "@/lib/shared/utils";
import {
  IconBell,
  IconBellFilled,
  IconCheck,
  IconExternalLink,
  IconAlertTriangle,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import Link from "next/link";
import { useState, useEffect, useRef, useCallback } from "react";
import { useBellOpenSignal } from "@/providers/notification-listener";

const SEVERITY_CONFIG = {
  critical: {
    icon: IconAlertTriangle,
    color: "text-red-500 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-500/20",
  },
  major: {
    icon: IconAlertTriangle,
    color: "text-orange-500 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-500/20",
  },
  minor: {
    icon: IconAlertCircle,
    color: "text-yellow-600 dark:text-yellow-400",
    bg: "bg-yellow-100 dark:bg-yellow-500/20",
  },
  warning: {
    icon: IconAlertCircle,
    color: "text-amber-500 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-500/20",
  },
  info: {
    icon: IconInfoCircle,
    color: "text-blue-500 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-500/20",
  },
};

function NotificationItem({
  notification,
  onMarkAsRead,
  isHighlighted = false,
}: {
  notification: InAppNotification;
  onMarkAsRead: (id: string) => void;
  isHighlighted?: boolean;
}) {
  // For alarm notifications, use severity config; for others, default to info
  const severity = notification.severity ?? "info";
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;
  const isUnread = !notification.read_at;

  // Derive display title: display_name for alarms, message for simple notifications
  const displayTitle =
    notification.display_name ||
    notification.title ||
    notification.message ||
    "Notification";

  // For alarm notifications, alarm_message is the detail; for others, message is the main content
  // Only show message as subtitle if we have a display_name (avoid duplicating message as both title and subtitle)
  const displayMessage =
    notification.notification_type === "alarm"
      ? notification.alarm_message || notification.message
      : notification.display_name
        ? notification.message
        : undefined;

  return (
    <div
      className={cn(
        "px-3 py-2.5 transition-colors",
        isUnread
          ? "bg-accent/50 hover:bg-accent"
          : "hover:bg-muted/50",
        isHighlighted && "ring-2 ring-primary ring-inset animate-pulse"
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className={cn("p-1.5 rounded-md shrink-0 mt-0.5", config.bg)}>
          <Icon className={cn("h-3.5 w-3.5", config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className={cn(
                "text-sm leading-snug break-words",
                isUnread ? "font-medium text-foreground" : "text-muted-foreground"
              )}
            >
              {displayTitle}
            </p>
            {isUnread && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0 -mr-1 hover:bg-background"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification.id);
                }}
              >
                <IconCheck className="h-3 w-3" />
              </Button>
            )}
          </div>
          {displayMessage && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 break-words">
              {displayMessage}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground/70 mt-1">
            {formatDistanceToNow(new Date(notification.created_at), {
              addSuffix: true,
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

function NotificationSkeleton() {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <Skeleton className="h-6 w-6 rounded-md shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-2.5 w-20" />
        </div>
      </div>
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const highlightedIdRef = useRef<string | null>(null);

  // Subscribe to bell_open signals from NotificationListener
  const {
    shouldOpenBell,
    clearOpenBell,
    highlightedNotification,
    clearHighlightedNotification,
  } = useBellOpenSignal();

  // Auto-open bell when signaled by notification listener
  useEffect(() => {
    if (shouldOpenBell) {
      setOpen(true);
      if (highlightedNotification) {
        highlightedIdRef.current = highlightedNotification.id;
      }
      clearOpenBell();
      // Clear highlight after a few seconds
      const timeout = setTimeout(() => {
        highlightedIdRef.current = null;
        clearHighlightedNotification();
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [shouldOpenBell, highlightedNotification, clearOpenBell, clearHighlightedNotification]);

  // Subscribe to real-time notification updates
  const { unreadCount: wsUnreadCount, isConnected: wsConnected } =
    useNotificationsWS();

  // Fetch unread count (lightweight, can poll frequently)
  // Disable polling when WebSocket is connected
  const { data: polledUnreadCount = 0 } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: inAppNotificationsApi.getUnreadCount,
    refetchInterval: wsConnected ? false : 30000, // Disable polling when WS connected
    staleTime: 10000,
  });

  // Use WebSocket count when available, fallback to polled count
  const unreadCount = wsUnreadCount ?? polledUnreadCount;

  // Fetch notifications when popover opens
  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => inAppNotificationsApi.getNotifications({ page_size: 5 }),
    enabled: open,
    staleTime: 10000,
  });

  // Close popover when all notifications have been read
  const closePopover = useCallback(() => {
    setOpen(false);
  }, []);

  const markAsReadMutation = useMutation({
    mutationFn: inAppNotificationsApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: inAppNotificationsApi.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      // Auto-close when all are marked as read
      closePopover();
    },
  });

  // Auto-close when unread count reaches 0 (after marking individual items as read)
  const prevUnreadCountRef = useRef(unreadCount);
  useEffect(() => {
    // Only close if we had unread notifications before and now we don't
    if (prevUnreadCountRef.current > 0 && unreadCount === 0 && open) {
      // Small delay to let user see the last notification marked as read
      const timeout = setTimeout(() => {
        closePopover();
      }, 500);
      return () => clearTimeout(timeout);
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, open, closePopover]);

  const notifications = data?.notifications || [];
  const hasUnread = unreadCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ""}`}
        >
          {hasUnread ? (
            <IconBellFilled className="h-5 w-5" />
          ) : (
            <IconBell className="h-5 w-5" />
          )}
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2 text-muted-foreground hover:text-foreground"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-[350px] overflow-y-auto">
          {isLoading ? (
            <div className="divide-y">
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 px-4 text-center text-sm text-muted-foreground">
              <IconBell className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={(id) => markAsReadMutation.mutate(id)}
                  isHighlighted={highlightedIdRef.current === notification.id}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t p-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs h-8 text-muted-foreground hover:text-foreground"
            asChild
          >
            <Link href="/notifications">
              View all notifications
              <IconExternalLink className="h-3 w-3 ml-1.5" />
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
